//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  setCustomThumbnail,
  uploadCoverImage,
} from '@apis/universal-image-actions/universalImageActionsApi';
import { SetCustomThumbnailRequestBody } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

export const useUploadCoverImage = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload: File) => {
      return uploadCoverImage(payload);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image uploaded successfully!',
          message: "You've successfully uploaded this cover image.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image upload failed',
          message:
            "Cover image couldn't be uploaded. Please check your selected photo and try again.",
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
};

export interface SetCustomThumbnailParams {
  cuid: Cuid;
  thumbnailFileId?: FileId;
  thumbnailOffsetMilliseconds?: number;
}

export const useSetCustomThumbnail = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cuid,
      thumbnailFileId,
      thumbnailOffsetMilliseconds,
    }: SetCustomThumbnailParams) => {
      const requestBody: SetCustomThumbnailRequestBody = {};

      if (thumbnailFileId) {
        requestBody.thumbnailFileId = thumbnailFileId;
      }

      if (thumbnailOffsetMilliseconds !== undefined) {
        requestBody.thumbnailOffsetMilliseconds = thumbnailOffsetMilliseconds;
      }

      const itemId = Cuid.toPartial(cuid);
      const result = await setCustomThumbnail(itemId, requestBody);
      return result;
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image updated successfully!',
          message: "You've successfully updated the cover image.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image update failed',
          message:
            "Cover image couldn't be updated. Please try again or contact support.",
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSettled: async (_, __, params) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.UniversalImageActions,
          QueryKeyConstants.UniversalImageActionsRights,
          QueryKeyConstants.UniversalImageActionsMetadata,
          params.cuid,
        ],
      });
    },
  });
};
